import { evoConsolidation, evoKeycloak, realm } from '$api'
export async function fetchFileAsServiceUser({
  queryKey: [_parts, partId, _, file_name],
}) {
  const { data } = await evoConsolidation.get(
    `/parts/${partId}/file?` +
      new URLSearchParams({
        file_name,
      }),
    {
      responseType: 'blob',
    },
  )
  return new Blob([data], { type: 'application/octet-stream' })
}
export async function getServiceProviderCompanies() {
  const res = await evoConsolidation.get(
    '/service-provider-materials/service_providers',
  )
  return res?.data
}
export async function getServiceProviderMaterialsByOperatorMateriaId({
  queryKey: [service_provider_name, operator_material_id, selectedColor],
}) {
  const requestBody = {
    service_provider_name,
    selected_color: selectedColor,
  }
  const { data } = await evoConsolidation.post(
    `/service-provider-materials/operator_materials/${operator_material_id}`,
    requestBody,
  )
  return data
}
export async function addServiceProviderMaterialToOrderPart({
  orderPartId,
  operatorAdditiveMaterialId,
  serviceProviderMaterialId,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/${orderPartId}/service_provider_material`,
    {
      operator_additive_material_id: operatorAdditiveMaterialId,
      service_provider_material_id: serviceProviderMaterialId,
    },
  )
  return data
}
export async function addServiceProviderToOrderPartList({
  orderPartListId,
  serviceProvider,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${orderPartListId}/set_service_provider_company`,
    { service_provider_company: serviceProvider },
  )
  return data
}

export async function addOperatorNoteToOrderPartList({
  orderPartListId,
  operatorNote,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${orderPartListId}/operator_note?` +
      new URLSearchParams({
        operator_note: operatorNote || '',
      }),
  )
  return data
}

export async function updateOrder({
  orderPartListId,
  serviceProvider,
  operatorNote,
  orderParts,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${orderPartListId}/update_order`,
    {
      service_provider_company: serviceProvider,
      operator_note: operatorNote ?? null,
      order_parts: orderParts.map((part) => ({
        order_part_id: part.orderPartId,
        operator_additive_material_id: part.operatorAdditiveMaterialId,
        service_provider_material_id: part.serviceProviderMaterialId,
      })),
    },
  )
  return data
}

export async function fetchFileAsServiceProvider({ queryKey: [_, file_name] }) {
  const { data } = await evoConsolidation.get(
    `/parts/files/?` +
      new URLSearchParams({
        file_name,
      }),
    {
      responseType: 'blob',
    },
  )
  return new Blob([data], { type: 'application/octet-stream' })
}

export async function getPartThumbnailAsServiceProvider({
  queryKey: [_, partId],
}) {
  const { data } = await evoConsolidation.get(`/parts/${partId}/thumbnail/`, {
    responseType: 'blob',
  })
  return data
}

export async function fetchOperatorAdditiveMaterialsByDwhIds({
  queryKey: [_, dwhMaterialIds],
}) {
  if (!dwhMaterialIds || dwhMaterialIds.length === 0) {
    return []
  }
  const { data } = await evoConsolidation.post(
    '/operator-additive-materials/by-dwh-materials',
    {
      dwh_material_ids: dwhMaterialIds,
    },
  )
  return data
}

export async function fetchAllBookedServices() {
  const res = await evoConsolidation.get('/booked-services/')
  return res?.data
}

export async function fetchBookedServiceKpis() {
  const { data } = await evoConsolidation.get(`/booked-services/kpis`)
  return data
}

export async function fetchAllBookedServicesQueries({
  queryKey: [
    _,
    { pageNr, pageSize, currentService, sorting = {}, clientPartId },
  ],
}) {
  const { key = 'time_stamp', order = 1 } = sorting
  const params = {
    page: pageNr,
    size: pageSize,
    service_name: currentService,
    key,
    order,
    client_part_id: clientPartId,
  }
  const paramsString = Object.entries(params)
    .filter(([_key, value]) => !!value)
    .map(([key, value]) => `${key}=${value}`)
    .join('&')
  const { data } = await evoConsolidation.get(
    `/booked-services/${paramsString ? `?${paramsString}` : ''}`,
  )
  return data
}

export async function fetchAllApplyBrandingParts() {
  const res = await evoConsolidation.get('/booked-services/apply-branding/')
  return res?.data
}

export async function fetchAllApplyTexturingParts() {
  const res = await evoConsolidation.get('/booked-services/apply-texturing/')
  return res?.data
}

export async function fetchAllGenerate3DModelParts() {
  const res = await evoConsolidation.get('/booked-services/generate-3d-model/')
  return res?.data
}

export async function fetchAllOrderPartsLists({
  queryKey: [_, { pageNr = 1, pageSize = 10, sorting = {}, filters = {} } = {}],
}) {
  const params = {
    page: pageNr,
    size: pageSize,
  }

  if (sorting.field && sorting.order) {
    params.key = sorting.field
    params.order = sorting.order === 'ascend' ? 1 : -1
  }

  const filtersArray = []
  if (filters.order_number) {
    filtersArray.push({
      parameter: 'order_number',
      logical_operator: 'contains',
      value: filters.order_number,
    })
  }
  if (filters.client_company) {
    filtersArray.push({
      parameter: 'client_company',
      logical_operator: 'contains',
      value: filters.client_company,
    })
  }
  if (filters.status) {
    filtersArray.push({
      parameter: 'status_info',
      logical_operator: 'contains',
      value: filters.status,
    })
  }

  const { data } = filtersArray.length
    ? await evoConsolidation.post(
        '/booked-services/order-parts-list/filter/',
        filtersArray,
        { params },
      )
    : await evoConsolidation.get('/booked-services/order-parts-list/', {
        params,
      })

  for (const orderPartsList of data.items) {
    for (const orderPart of orderPartsList.list_of_orderparts) {
      for (const offer of orderPart.provider_offers) {
        for (const priceRule of offer.price_rules) {
          priceRule.id = crypto.randomUUID()
        }
        for (const productionTimeRule of offer.production_time_rules) {
          productionTimeRule.id = crypto.randomUUID()
        }
      }
    }
  }

  return data
}

export async function setClientAcceptedOffer({
  order_parts_list_id,
  client_accepted_offer,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${order_parts_list_id}/client-accepted-offer?` +
      new URLSearchParams({
        client_accepted_offer,
      }),
  )
  return data
}

export async function setProviderAcceptedOffer({
  order_part_list_id,
  provider_accepted_offer,
  provider_rejection_reason,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${order_part_list_id}/provider-accepted-offer?` +
      new URLSearchParams({
        provider_accepted_offer,
        provider_rejection_reason,
      }),
  )
  return data
}

export const fetchFcServices = async ({ queryKey }) => {
  const [_, partId] = queryKey
  const { data } = await evoConsolidation.get(`/feasibility-checks/${partId}`)
  return data
}

export const fetchMetaFileName = async (
  file_id,
  file_name,
  isServiceProvider,
) => {
  const response = await evoConsolidation.head(
    isServiceProvider
      ? `${consolidationPart}files/?file_name=${file_name}`
      : `${consolidationPart}${file_id}/file?file_name=${file_name}`,
  )

  const contentDisposition = response.headers['content-disposition']
  const match = contentDisposition?.match(/filename="?(.+?)"?$/)
  const originalName = match ? match[1] : undefined

  return originalName
}

export async function fetchAllFCServices() {
  const res = await evoConsolidation.get('/feasibility-checks/')
  return res?.data
}

export async function fetchAllOrderParts() {
  const res = await evoConsolidation.get('/booked-services/order-part/')
  return res?.data
}

export async function setOrderPartProviderOffers({
  order_part_id,
  provider_offers,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-part/${order_part_id}/provider_offers_as_service_provider`,
    provider_offers,
  )
  return data
}

export async function updateOrderPartProviderOffers({
  order_part_id,
  provider_offers,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-part/${order_part_id}/provider_offers_as_client`,
    provider_offers,
  )
  return data
}

export async function setOrderPartsListDateOfExpiration({
  order_parts_list_id,
  date_of_expiration,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${order_parts_list_id}/date_of_expiration`,
    date_of_expiration,
  )
  return data
}

export async function setOrderPartsListProviderNote({
  order_parts_list_id,
  provider_note,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-parts-list/${order_parts_list_id}/provider_note?` +
      new URLSearchParams({
        provider_note: provider_note || '',
      }),
  )
  return data
}

export async function setOrderPartRejectedByClient({
  order_part_id,
  rejected_by_client,
}) {
  const { data } = await evoConsolidation.patch(
    `/booked-services/order-part/${order_part_id}/rejected_by_client?` +
      new URLSearchParams({
        rejected_by_client,
      }),
  )
  return data
}

export async function fetchAllServices() {
  const res = await evoConsolidation.get('/services/')
  return res?.data
}

export async function fetchAllPartsConsolidation() {
  const res = await evoConsolidation.get('/parts/')
  return res?.data
}

export async function fetchLegalTexts({ queryKey: [_, language] }) {
  const res = await evoConsolidation.get('/legal_texts/', {
    headers: {
      'Accept-Language': language,
    },
  })
  return res?.data
}
export async function fetchUnreadNotifications() {
  const res = await evoConsolidation.get('/notifications/?read=false')
  return res?.data
}
export async function fetchReadNotifications() {
  const res = await evoConsolidation.get('/notifications/?read=true')
  return res?.data
}
export async function fetchAllNotifications() {
  const res = await evoConsolidation.get('/notifications/')
  return res?.data
}

export async function markNotificationAsRead(notification_id) {
  const { data } = await evoConsolidation.patch(
    `/notifications/${notification_id}/read`,
  )
  return data
}

export async function markAllNotificationsAsRead() {
  const { data } = await evoConsolidation.patch(
    `/notifications/mark-all-as-read`,
  )
  return data
}

export const notifications = `/notifications/`

export const postServices = `/services/`

export const postPartsConsolidation = `/parts/`

export const putBookedService = `/booked-services/`

export const postOrder = `/booked-services/order-part/`

export const postApplyBranding = `/booked-services/apply-branding/`

export const postApplyTexturing = `/booked-services/apply-texturing/`

export const postGenerate3DModel = `/booked-services/generate-3d-model/`

export const postSTLFileClient = `/parts/`

export const postFcService = `/feasibility-checks/` //deprecated

export const postFeasibilityCheck = `/booked-services/feasibility-check/`

export const postOrderList = `/booked-services/order-parts-list/`

export const consolidationPart = `/parts/`

//keycloak user endpoints

export const fetchLoggedInUser = async ({ queryKey }) => {
  const [_] = queryKey
  const { data } = await evoKeycloak.get(`/realms/${realm}/account`)
  return data
}

export const fetchLoggedInUserInfo = async ({ queryKey }) => {
  const [_] = queryKey
  const { data } = await evoKeycloak.get(
    `/realms/${realm}/protocol/openid-connect/userinfo`,
  )
  return data
}

export const fetchUser = async ({ queryKey }) => {
  const [_, userId] = queryKey
  const { data } = await evoKeycloak.get(
    `/admin/realms/${realm}/users/${userId}`,
  )
  return data
}

export const fetchUsers = async () => {
  const { data } = await evoKeycloak.get(`/admin/realms/${realm}/users`)
  return data
}

export const fetchUserRole = async ({ queryKey }) => {
  const [_, userId] = queryKey
  const { data } = await evoKeycloak.get(
    `/admin/realms/${realm}/users/${userId}/role-mappings/realm`,
  )
  return data
}

export const fetchCompanyOfUser = async ({ queryKey }) => {
  const [_, userId] = queryKey
  const { data } = await evoKeycloak.get(
    `/admin/realms/${realm}/users/${userId}/groups`,
  )
  return data
}

export const fetchCompanyData = async ({ queryKey }) => {
  const [_, groupId] = queryKey
  const { data } = await evoKeycloak.get(
    `/admin/realms/${realm}/groups/${groupId}`,
  )
  return data
}

export const fetchUsersOfCompany = async ({ queryKey }) => {
  const [_, companyId] = queryKey
  const { data } = await evoKeycloak.get(
    `/admin/realms/${realm}/groups/${companyId}/members`,
  )
  return data
}

export async function fetchAllCompanies() {
  const res = await evoKeycloak.get(`/admin/realms/${realm}/groups`)
  return res?.data
}

export async function fetchAllRoles() {
  const res = await evoKeycloak.get(`/admin/realms/${realm}/roles`)
  return res?.data
}

// create user
export const postUser = async ({
  firstName,
  lastName,
  email,
  username,
  company,
  password,
}) => {
  const response = await evoKeycloak.post(
    `/admin/realms/${realm}/users`,
    {
      firstName: firstName,
      lastName: lastName,
      email: email,
      username: username,
      groups: [company],
      credentials: [
        {
          type: 'password',
          value: password,
          temporary: false,
        },
      ],
      enabled: true,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )

  const location_url = response?.headers?.location

  const [_loc, userId] = location_url.split(`/admin/realms/${realm}/users/`)

  return {
    id: userId,
  }
}

// update user
export const putUser = async ({ userId, firstName, lastName, email }) => {
  const response = await evoKeycloak.put(
    `/admin/realms/${realm}/users/${userId}`,
    {
      firstName: firstName,
      lastName: lastName,
      email: email,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )

  return response
}

export const deleteUser = async ({ userId }) => {
  const response = await evoKeycloak.delete(
    `/admin/realms/${realm}/users/${userId}`,
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )

  return response
}

export const postRoleMapping = async ({ userId, roleId, roleName }) => {
  const response = await evoKeycloak.post(
    `/admin/realms/${realm}/users/${userId}/role-mappings/realm`,
    [
      {
        id: roleId,
        name: roleName,
      },
    ],
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )

  return response
}

export const deleteRoleMapping = async ({ userId, roleId, roleName }) => {
  const response = await evoKeycloak.delete(
    `/admin/realms/${realm}/users/${userId}/role-mappings/realm`,
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: [
        {
          id: roleId,
          name: roleName,
        },
      ],
    },
  )
  return response
}

export async function fetchAllPrinters({ queryKey }) {
  const [_key, paramsObj = {}] = queryKey
  const params = {
    page: paramsObj.page || 1,
    size: paramsObj.size || 10,
  }
  const res = await evoConsolidation.get('/printers/', { params })
  return res?.data
}

export async function fetchAllMaterials({ queryKey }) {
  const [_key, paramsObj = {}] = queryKey

  const apiQueryParams = {
    page: paramsObj.page || 1,
    size: paramsObj.size || 10,
    key: 'name',
    order: 1,
  }

  if (paramsObj.sort_by) {
    if (paramsObj.sort_by.startsWith('-')) {
      apiQueryParams.key = paramsObj.sort_by.substring(1)
      apiQueryParams.order = -1
    } else {
      apiQueryParams.key = paramsObj.sort_by
      apiQueryParams.order = 1
    }
  }

  let response
  const baseRequestUrl = '/dwh_materials/'

  if (paramsObj.name && paramsObj.name.trim() !== '') {
    const filterEndpointUrl = `${baseRequestUrl}filter/`
    const filterBody = [
      {
        parameter: 'name',
        logical_operator: 'contains',
        value: paramsObj.name,
      },
    ]
    response = await evoConsolidation.post(filterEndpointUrl, filterBody, {
      params: {
        key: apiQueryParams.key,
        order: apiQueryParams.order,
        page: apiQueryParams.page,
        size: apiQueryParams.size,
      },
    })
  } else {
    response = await evoConsolidation.get(baseRequestUrl, {
      params: apiQueryParams,
    })
  }

  return response.data
}

export async function fetchMaterialById(materialId) {
  const { data } = await evoConsolidation.get(`/dwh_materials/${materialId}/`)
  return data
}

export const fetchLabels = async ({
  queryKey: [_, { exclude_system_managed }],
}) => {
  const { data } = await evoConsolidation.get(
    `/labels/?` +
      new URLSearchParams({
        exclude_system_managed,
      }),
  )
  return data
}

export const fetchLabelsForBookedService = async ({
  queryKey: [_, bookedServiceId],
}) => {
  const { data } = await evoConsolidation.get(
    `/booked-services/${bookedServiceId}/labels`,
  )
  return data
}

export const removeLabelFromBookedService = async ({
  bookedServiceId,
  labelName,
}) => {
  const { data } = await evoConsolidation.delete(
    `/booked-services/${bookedServiceId}/labels/${labelName}`,
  )
  return data
}

export const addLabelToBookedService = async ({
  bookedServiceId,
  labelName,
}) => {
  const { data } = await evoConsolidation.post(
    `/booked-services/${bookedServiceId}/labels`,
    {
      label: labelName,
    },
  )
  return data
}

export async function postDwhMaterial(materialData) {
  const { data } = await evoConsolidation.post('/dwh_materials/', materialData)
  return data
}

export async function uploadDwhMaterialSpecFile({
  materialId,
  file,
  onProgress,
}) {
  const formData = new FormData()
  formData.append('file', file)

  const config = {
    headers: { 'content-type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      onProgress({ percent: (event.loaded / event.total) * 100 })
    },
  }

  const { data } = await evoConsolidation.post(
    `/dwh_materials/${materialId}/spec-file`,
    formData,
    config,
  )
  return data
}

export async function uploadOperatorAdditiveMaterialSpecFile({
  materialId,
  file,
  onProgress,
}) {
  const formData = new FormData()
  formData.append('file', file)

  const config = {
    headers: { 'content-type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      onProgress({ percent: (event.loaded / event.total) * 100 })
    },
  }

  const { data } = await evoConsolidation.post(
    `/operator-additive-materials/${materialId}/spec-file`,
    formData,
    config,
  )
  return data
}

export async function uploadServiceProviderMaterialSpecFile({
  materialId,
  file,
  onProgress,
}) {
  const formData = new FormData()
  formData.append('file', file)

  const config = {
    headers: { 'content-type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      onProgress({ percent: (event.loaded / event.total) * 100 })
    },
  }

  const { data } = await evoConsolidation.post(
    `/service-provider-materials/${materialId}/spec-file`,
    formData,
    config,
  )
  return data
}

export async function fetchServiceProviderMaterials({ queryKey }) {
  const [_key, providerName] = queryKey || []
  let config = {}

  if (providerName) {
    config.params = {
      provider_name: providerName,
    }
  }
  const { data } = await evoConsolidation.get(
    `/service-provider-materials/`,
    config,
  )
  return data
}

export async function archiveServiceProviderAdditiveMaterial(materialId) {
  const { data } = await evoConsolidation.patch(
    `/service-provider-materials/${materialId}/archive`,
  )
  return data
}

export async function syncServiceProviderMaterials({ providerName }) {
  const { data } = await evoConsolidation.post(
    `/service-provider-materials/${providerName}/sync`,
  )
  return data
}

export async function fetchServiceProviderMaterialsKpiCount() {
  const { data } = await evoConsolidation.get(
    '/service-provider-materials/kpi/count',
  )
  return data
}

export async function downloadDwhMaterialSpecFile(materialId) {
  const response = await evoConsolidation.get(
    `/dwh_materials/${materialId}/spec-file`,
    {
      responseType: 'blob',
    },
  )

  const contentDisposition = response.headers['content-disposition']
  let filename = 'specification.pdf'
  if (contentDisposition) {
    const filenameStarMatch = contentDisposition.match(/filename\*=utf-8''(.+)/)
    if (filenameStarMatch && filenameStarMatch.length > 1) {
      filename = decodeURIComponent(filenameStarMatch[1])
    } else {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1]
      }
    }
  }

  return { blob: response.data, filename }
}

export async function downloadOperatorAdditiveMaterialSpecFile(materialId) {
  const response = await evoConsolidation.get(
    `/operator-additive-materials/${materialId}/spec-file`,
    {
      responseType: 'blob',
    },
  )

  const contentDisposition = response.headers['content-disposition']
  let filename = 'specification.pdf'
  if (contentDisposition) {
    const filenameStarMatch = contentDisposition.match(/filename\*=utf-8''(.+)/)
    if (filenameStarMatch && filenameStarMatch.length > 1) {
      filename = decodeURIComponent(filenameStarMatch[1])
    } else {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1]
      }
    }
  }

  return { blob: response.data, filename }
}

export async function downloadServiceProviderMaterialSpecFile(materialId) {
  const response = await evoConsolidation.get(
    `/service-provider-materials/${materialId}/spec-file`,
    {
      responseType: 'blob',
    },
  )

  const contentDisposition = response.headers['content-disposition']
  let filename = 'specification.pdf'
  if (contentDisposition) {
    const filenameStarMatch = contentDisposition.match(/filename\*=utf-8''(.+)/)
    if (filenameStarMatch && filenameStarMatch.length > 1) {
      filename = decodeURIComponent(filenameStarMatch[1])
    } else {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1]
      }
    }
  }

  return { blob: response.data, filename }
}

export async function discardAllOrderPartsByClientMaterialName(
  client_material_name,
) {
  const response = await evoConsolidation.patch(
    `/booked-services/order-parts-list/discard_offers`,
    null,
    {
      params: {
        client_material_name,
      },
    },
  )

  return response
}

export async function createServiceProviderMaterials(materials) {
  const { data } = await evoConsolidation.post(
    '/service-provider-materials/multiple',
    materials,
  )
  return data
}

export async function createServiceProviderMaterial(material) {
  const { data } = await evoConsolidation.post(
    '/service-provider-materials/',
    material,
  )
  return data
}

export async function fetchAutocompleteServiceProviderMaterials(searchTerm) {
  if (!searchTerm || searchTerm.length < 2) return []
  const { data } = await evoConsolidation.get(
    `/service-provider-materials/autocomplete/`,
    {
      params: { search: searchTerm },
    },
  )
  return data
}

export async function mapOperatorAdditiveMaterialsToServiceProviderMaterial({
  additiveMaterialId,
  operatorAdditiveMaterialIds,
}) {
  let idsArray
  if (operatorAdditiveMaterialIds instanceof Set) {
    idsArray = Array.from(operatorAdditiveMaterialIds)
  } else if (Array.isArray(operatorAdditiveMaterialIds)) {
    idsArray = operatorAdditiveMaterialIds
  } else {
    idsArray = []
  }

  const payload = {
    operator_additive_material_ids: idsArray,
  }

  const { data } = await evoConsolidation.put(
    `/service-provider-materials/${additiveMaterialId}/map-operator-additive-materials`,
    payload,
  )

  return data
}

export async function createClientNotification({ title, body, lang }) {
  const { data } = await evoConsolidation.post(
    '/notifications/create-from-client',
    {
      title,
      body,
      lang,
    },
  )
  return data
}

export async function fetchAllOperatorAdditiveMaterials({ queryKey }) {
  const [_key, paramsObj = {}] = queryKey

  const apiQueryParams = {
    page: paramsObj.page || 1,
    size: paramsObj.size || 10,
    key: 'material_name',
    order: 1,
  }

  if (paramsObj.sort_by) {
    if (paramsObj.sort_by.startsWith('-')) {
      apiQueryParams.key = paramsObj.sort_by.substring(1)
      apiQueryParams.order = -1
    } else {
      apiQueryParams.key = paramsObj.sort_by
      apiQueryParams.order = 1
    }
  }

  let response
  const baseRequestUrl = '/operator-additive-materials/'

  if (paramsObj.material_name && paramsObj.material_name.trim() !== '') {
    const filterEndpointUrl = `${baseRequestUrl}filter/`
    const filterBody = [
      {
        parameter: 'material_name',
        logical_operator: 'contains',
        value: paramsObj.material_name,
      },
    ]
    response = await evoConsolidation.post(filterEndpointUrl, filterBody, {
      params: {
        key: apiQueryParams.key,
        order: apiQueryParams.order,
        page: apiQueryParams.page,
        size: apiQueryParams.size,
      },
    })
  } else {
    response = await evoConsolidation.get(baseRequestUrl, {
      params: apiQueryParams,
    })
  }

  return response.data
}

export async function mapDwhMaterialsToOperatorAdditiveMaterial({
  operatorAdditiveMaterialId,
  dwhMaterialIds,
}) {
  let idsArray
  if (dwhMaterialIds instanceof Set) {
    idsArray = Array.from(dwhMaterialIds)
  } else if (Array.isArray(dwhMaterialIds)) {
    idsArray = dwhMaterialIds
  } else {
    idsArray = []
  }

  const payload = {
    dwh_material_ids: idsArray,
  }

  const { data } = await evoConsolidation.put(
    `/operator-additive-materials/${operatorAdditiveMaterialId}/map-dwh-materials`,
    payload,
  )

  return data
}

export async function createOperatorAdditiveMaterials(materials) {
  const { data } = await evoConsolidation.post(
    '/operator-additive-materials/multiple',
    materials,
  )
  return data
}
export async function archiveOperatorAdditiveMaterial(materialId) {
  const { data } = await evoConsolidation.patch(
    `/operator-additive-materials/${materialId}/archive`,
  )
  return data
}
export async function createOperatorAdditiveMaterial(material) {
  const { data } = await evoConsolidation.post(
    '/operator-additive-materials/',
    material,
  )
  return data
}

export async function fetchAutocompleteOperatorAdditiveMaterials(searchTerm) {
  if (!searchTerm || searchTerm.length < 2) return []
  const { data } = await evoConsolidation.get(
    `/operator-additive-materials/autocomplete/`,
    {
      params: { search: searchTerm },
    },
  )
  return data
}

export async function fetchOperatorAdditiveMaterialsKpiCount() {
  const { data } = await evoConsolidation.get(
    '/operator-additive-materials/kpi/count',
  )
  return data
}

export async function fetchAllServiceProviderAdditiveMaterials({ queryKey }) {
  const [_key, paramsObj = {}] = queryKey

  const apiQueryParams = {
    page: paramsObj.page || 1,
    size: paramsObj.size || 10,
    key: 'material_name',
    order: 1,
  }

  if (paramsObj.sort_by) {
    if (paramsObj.sort_by.startsWith('-')) {
      apiQueryParams.key = paramsObj.sort_by.substring(1)
      apiQueryParams.order = -1
    } else {
      apiQueryParams.key = paramsObj.sort_by
      apiQueryParams.order = 1
    }
  }

  let response
  const baseRequestUrl = '/service-provider-materials/'

  if (paramsObj.material_name && paramsObj.material_name.trim() !== '') {
    const filterEndpointUrl = `${baseRequestUrl}filter/`
    const filterBody = [
      {
        parameter: 'material_name',
        logical_operator: 'contains',
        value: paramsObj.material_name,
      },
    ]
    response = await evoConsolidation.post(filterEndpointUrl, filterBody, {
      params: {
        key: apiQueryParams.key,
        order: apiQueryParams.order,
        page: apiQueryParams.page,
        size: apiQueryParams.size,
      },
    })
  } else {
    response = await evoConsolidation.get(baseRequestUrl, {
      params: apiQueryParams,
    })
  }

  return response.data
}

export async function suggestDwhMaterial(formData) {
  const { data } = await evoConsolidation.post(
    '/dwh_materials/suggest',
    formData,
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    },
  )
  return data
}

export async function fetchAllColors() {
  const { data } = await evoConsolidation.get('/colors/')
  return data
}

export async function updateServiceProviderMaterial({ id, ...data }) {
  const { data: response } = await evoConsolidation.put(
    `/service-provider-materials/${id}`,
    data,
  )
  return response
}

// APIS FOR OPERATOR FORWARD ORDER PAGE

// Forward the order part with Service Provider Material
export async function forwardOrderPart({
  orderPartId,
  serviceProviderMaterialId,
  serviceProviderId,
}) {
  const { data } = await evoConsolidation.post(
    `/booked-services/order-part/${orderPartId}/forward`,
    {
      service_provider_material_id: serviceProviderMaterialId,
      service_provider_id: serviceProviderId,
    },
  )
  return data
}

export async function fetchPostProcessingMethodWithOamId(oam_id) {
  const { data } = await evoConsolidation.get(
    `/operator-additive-materials/${oam_id}/post-processing-methods`,
  )
  return data
}

export async function fetchAvailableColorsWithOamId(oam_id) {
  const { data } = await evoConsolidation.get(
    `/operator-additive-materials/${oam_id}/available-colors`,
  )
  return data
}

export async function getMaterialChangeLogForOrderPart({ order_part_id }) {
  const { data } = await evoConsolidation.get(
    `/change-logs/order-part/${order_part_id}/operator-additive-material-change`,
  )
  return data
}
export async function fetchMlPrediction(file) {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await evoConsolidation.post('/ml/predict', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return data
}

export async function fetchTextures() {
  const res = await evoConsolidation.get(
    '/booked-services/apply-texturing/textures',
  )
  return res?.data
}

export async function fetchTextureFile(textureId) {
  const res = await evoConsolidation.get(
    `/booked-services/apply-texturing/textures/${textureId}/file`,
    {
      responseType: 'blob',
    },
  )
  return res?.data
}

export async function fetchServiceConfigByName(serviceName) {
  const res = await evoConsolidation.get(
    `/services/service-config/${serviceName}`,
  )
  return res?.data
}

export async function uploadServiceProviderFile(
  file,
  serviceName,
  serviceId,
  onProgress,
) {
  const formData = new FormData()
  formData.append('file', file)
  let endpoint = null
  if (serviceName == 'Apply Branding')
    endpoint = `${postApplyBranding}${serviceId}`

  if (serviceName == 'Apply Texturing')
    endpoint = `${postApplyTexturing}${serviceId}`

  if (serviceName == 'Generate 3D Model')
    endpoint = `${postGenerate3DModel}${serviceId}/file`

  if (!endpoint) throw new Error(`No upload endpoint for ${serviceName}`)

  const config = {
    onUploadProgress: (event) => {
      onProgress({ percent: (event.loaded / event.total) * 100 })
    },
  }

  const { data } = await evoConsolidation.put(endpoint, formData, config)
  return data
}

export async function removeBookedServiceProviderFile(
  bookedServiceId,
  filename,
) {
  return await evoConsolidation.delete(
    `${putBookedService}${bookedServiceId}`,
    {
      params: { filename: filename },
    },
  )
}

export async function fetchBookedServiceProviderMetaFile(bookedServiceId) {
  const { data } = await evoConsolidation.get(
    `${putBookedService}${bookedServiceId}/meta-file`,
  )
  return data
}
